import React from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import { AUTH_CONTEXT_TYPE } from "../../../utils/authPortal";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { generateDeviceId } from "../../../utils/Auth";
import messages from "../../../assets/locale/messages";
import { loginRequest } from "../../../store/Auth/actions";
import Input from "../../../components/Input";
import { UIButton, UICheckbox } from "../../../components/ui";
import { getFirebaseToken } from "../../../firebase/helpers";

import "./Login.scss";

const resolveUserTypeForSubmit = (portalType, signInAsSupervisor = false) => {
  if (portalType === AUTH_CONTEXT_TYPE.ADMIN && signInAsSupervisor) {
    return "supervisor";
  }
  switch (portalType) {
    case AUTH_CONTEXT_TYPE.COMPANY:
    case AUTH_CONTEXT_TYPE.CLIENT:
      return "client";
    case AUTH_CONTEXT_TYPE.ADMIN:
    default:
      return "admin";
  }
};

const resolveForgetPasswordPath = (portalType) => {
  switch (portalType) {
    case AUTH_CONTEXT_TYPE.COMPANY:
      return ROUTE_PATHS.companyForgetPassword;
    case AUTH_CONTEXT_TYPE.CLIENT:
      return ROUTE_PATHS.clientForgetPassword;
    case AUTH_CONTEXT_TYPE.ADMIN:
    default:
      return ROUTE_PATHS.forgetPassword;
  }
};

const resolveFailureRedirectPath = (portalType) => {
  switch (portalType) {
    case AUTH_CONTEXT_TYPE.COMPANY:
      return ROUTE_PATHS.companyLogin;
    case AUTH_CONTEXT_TYPE.CLIENT:
      return ROUTE_PATHS.clientLogin;
    case AUTH_CONTEXT_TYPE.ADMIN:
    default:
      return ROUTE_PATHS.login;
  }
};

const Login = ({ type = AUTH_CONTEXT_TYPE.ADMIN }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { login } = messages[lang];
  const isCompany = type === AUTH_CONTEXT_TYPE.COMPANY;
  const isAdminPortal = type === AUTH_CONTEXT_TYPE.ADMIN;
  const forgetPasswordPath = resolveForgetPasswordPath(type);

  const dispatch = useDispatch();

  const {
    values,
    setFieldValue,
    handleSubmit,
    touched,
    errors,
    setFieldTouched,
    dirty,
    isValid,
  } = useFormik({
    initialValues: {
      email: "",
      password: "",
      signInAsSupervisor: false,
    },
    validationSchema: Yup.object({
      email: Yup.string().email("validEmailFormat").required("emailRequired"),
      password: Yup.string().required("passwordRequired"),
    }),
    onSubmit: ({ email, password, signInAsSupervisor }) => {
      const userType = resolveUserTypeForSubmit(type, signInAsSupervisor);
      dispatch(
        loginRequest({
          data: {
            email,
            password,
            device_id: generateDeviceId(),
            fcm_token: getFirebaseToken(),
          },
          userType,
          authPortal: type,
          failureRedirectPath: resolveFailureRedirectPath(type),
        }),
      );
    },
  });

  return (
    <div className="auth-form-shell login-page w-100" data-auth-portal={type}>
      <header className="auth-form-shell__header login-page__header">
        <h1 className="auth-layout__card__title">
          {login.pageTitle}
        </h1>
        <p className="auth-layout__card__subtitle">
          {login.pageSubtitle}
        </p>
      </header>

      <form
        className="auth-form-shell__form login-page__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="login-page__field">
          <Input
            id="email"
            name="email"
            type="email"
            label={login.labels.emailAddress}
            placeholder={login.placeholders.email}
            required
            onChange={(value) => {
              setFieldTouched("email");
              setFieldValue("email", value.trim());
            }}
            value={values.email}
            labelClassName="login-page__label font-urbanist-semibold"
            inputWrapperClass="login-page__input-wrap"
            isInputHasErr={!!(touched.email && errors.email)}
            errMsg={errors.email}
            fullWidth
          />
        </div>
        <div className="login-page__field">
          <Input
            id="password"
            name="password"
            type="password"
            label={login.labels.password}
            placeholder={login.placeholders.password}
            required
            onChange={(value) => {
              setFieldTouched("password");
              setFieldValue("password", value.trim());
            }}
            value={values.password}
            labelClassName="login-page__label font-urbanist-semibold"
            inputWrapperClass="login-page__input-wrap"
            inputClass="login-page__password-input"
            isInputHasErr={!!(touched.password && errors.password)}
            errMsg={errors.password}
            fullWidth
            isPasswordInput
          />
        </div>

        <div
          className={`login-page__meta-row${
            isAdminPortal ? "" : " login-page__meta-row--end-only"
          }`}
        >
          {isAdminPortal && (
            <div className="login-page__supervisor">
              <UICheckbox
                name="signInAsSupervisor"
                label={login.labels.signInAsSupervisor}
                checked={values.signInAsSupervisor}
                onChange={(checked) =>
                  setFieldValue("signInAsSupervisor", checked)
                }
              />
            </div>
          )}
          <p className="auth-inline-prompt auth-inline-prompt--sm login-page__forgot-row mb-0">
            <span className="auth-inline-prompt__text">
              {login.labels.forgotPasswordPrefix}
            </span>{" "}
            <Link to={forgetPasswordPath} className="auth-inline-prompt__link">
              {login.labels.resetPasswordLink}
            </Link>
          </p>
        </div>
        <UIButton
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          disabled={!dirty || !isValid}
        >
          {login.labels.logIn}
        </UIButton>
        {isCompany && (
          <p className="auth-inline-prompt auth-inline-prompt--md auth-inline-prompt--center login-page__register mb-0">
            <span className="auth-inline-prompt__text">
              {login.labels.registerFooter}
            </span>{" "}
            <Link
              to={ROUTE_PATHS.companySignup}
              className="auth-inline-prompt__link"
            >
              {login.labels.registerCompany}
            </Link>
          </p>
        )}
      </form>
    </div>
  );
};

Login.propTypes = {
  type: PropTypes.oneOf(Object.values(AUTH_CONTEXT_TYPE)),
};

export default Login;
